//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ClientPurchaseOptionsSummary,
  GetClientPurchaseOptionsSummaryRequest,
  GetClientPurchaseOptionsSummaryResponse,
  GetSamplingOpportunitiesResponse,
  SamplingOpportunitiesResponse,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { deserializeOpportunities } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const SAMPLING_DEFAULT_ENDPOINT = '/api/v1/clients';

const samplingFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${SAMPLING_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );

/**
 * Get purchase options
 *
 * GET /api/v1/clients/opportunities
 */
export const getSamplingOpportunities = async (
  init?: RequestInit,
): Promise<SamplingOpportunitiesResponse> => {
  const res = await samplingFetch(`/opportunities`, {
    ...init,
    method: 'GET',
  });

  const samplingOpportunitiesResponse =
    await res.json<GetSamplingOpportunitiesResponse>((res) => ({
      data: {
        ...res.data,
        opportunities: deserializeOpportunities(res.data.opportunities),
      },
    }));

  return samplingOpportunitiesResponse.data;
};

/**
 * Get purchase options summary
 *
 * GET /api/v1/clients/opportunities/summary
 */
export const getSamplingOpportunitiesSummary = async (
  req: GetClientPurchaseOptionsSummaryRequest,
  init?: RequestInit,
): Promise<ClientPurchaseOptionsSummary> => {
  const params = makeParams(req);
  const res = await samplingFetch(`/opportunities/summary?${params}`, {
    ...init,
    method: 'GET',
  });

  const summaryResponse =
    await res.json<GetClientPurchaseOptionsSummaryResponse>();
  return summaryResponse.data;
};
